import {
  DropdownButton,
  DropdownButtonType,
} from '@tableau/widgets';
import React from 'react';
import { classes } from 'typestyle';

import { Telemetry } from '~commonInfra/services/Telemetry';
import {
  FavoritesMenuNavigateToFavoriteEvent,
  FavoritesMenuOpenEvent,
  TelemetryEvents,
} from '~commonInfra/telemetryEvents/TelemetryEvents';
import { Location } from '~commonInfra/utils/Location';
import { Messages } from '~src/codegen/Localize';
import { favoritesSpaceDefaultSortOrder } from '~src/favorites/models/FavoritesSortOptions';
import { Favorites } from '~src/favorites/resources/Favorites';
import { IFavorite } from '~src/favorites/types/IFavorite';
import { HeaderIcons } from '~src/spacesPlatform/components/header/HeaderIcons';
import { PageHeaderStyles } from '~src/spacesPlatform/components/layout/PageHeaderStyles';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';

import { FavoritesList, getFavoriteKey } from './FavoritesList';
import cssVar = PageHeaderStyles.CssVariables.cssVar;
import CssVariables = PageHeaderStyles.CssVariables;
import backgroundHover = PageHeaderStyles.backgroundHover;


// we just want to grab all favorites, we usually use 1000 as max number of items to fetch
const PAGESIZE_MAX_SLICE: number = 1000;
const favoritesMenuPopupId = 'favorites-menu-popup';

export interface FavoritesDropdownProps {
  readonly siteUrls: SpacesSiteUrls;
}

export interface FavoritesDropdownState {
  readonly favorites: IFavorite[];
  readonly favoritesRequestComplete: boolean;
  readonly buttonHover: boolean;
  readonly panelOpened: boolean;
}

export class FavoritesDropdown extends React.Component<FavoritesDropdownProps, FavoritesDropdownState> {
  private unmounted: boolean = false;

  public constructor(props: FavoritesDropdownProps) {
    super(props);
    this.state = {favorites: [], favoritesRequestComplete: false, buttonHover: false, panelOpened: false};
  }

  public componentWillUnmount() {
    this.unmounted = false;
  }

  public render(): JSX.Element {
    const buttonClasses = classes(
      PageHeaderStyles.headerDropdownButton,
      backgroundHover
    );
    return (
      <div onMouseEnter={this.onMouseEnter} onMouseLeave={this.onMouseLeave}>
        <DropdownButton
          aria-controls={favoritesMenuPopupId}
          aria-haspopup={'menu'}
          aria-label={Messages.favoritesMenu()}
          closeOnPopupBlur={true}
          focusOnClose={true}
          useDarkStyling={true}
          label={undefined}
          testId={'favorites-menu'}
          type={DropdownButtonType.Icon}
          children={this.getButtonContent()}
          popupContent={this.getList()}
          className={buttonClasses}
          title={this.closePanel ? Messages.favoritesMenuHide() : Messages.favoritesMenuShow()}
          onOpen={this.handleOpen}
          onClose={this.handleClose}
          popupAlignment={'right'}
        />
      </div>);
  }

  private getList = () => {
    return (
      <FavoritesList
        id={favoritesMenuPopupId}
        onSelect={this.handleChange}
        urlForFavorite={this.urlForFavorite}
        items={this.state.favorites}
        favoritesRequestCompleted={this.state.favoritesRequestComplete} />
    );
  };

  private closePanel: () => void;

  private handleOpen = (done: () => void): void => {
    // save close panel for later
    this.closePanel = done;

    const favoritesFetcher = Favorites.fetchFromExploreFavoriteContentForUser();

    favoritesFetcher
      .withOrder(favoritesSpaceDefaultSortOrder)
      .getSlice(0, PAGESIZE_MAX_SLICE)
      .then((result) => {
        Telemetry.sendEvent<FavoritesMenuOpenEvent>({
          type: TelemetryEvents.FavoritesMenuOpen,
          favoriteItemCount: result.total,
        });
        this.updateState({favorites: result.items, favoritesRequestComplete: true, panelOpened: true});
      }).catch(() => {
        this.updateState({favoritesRequestComplete: true, panelOpened: true});
      });
  };

  private handleClose = (): void => {
    if (this.closePanel) {
      this.closePanel();
      this.closePanel = null;
      this.updateState({panelOpened: false});
    }
  };

  private urlForFavorite = (favorite: IFavorite): string => {
    let redirectUrl = '';
    switch(favorite.objectType) {
      case 'datasource':
        if (favorite.objectId === '') {
          redirectUrl = this.props.siteUrls.favoriteDatasourceDoesntExistUrl(favorite.objectIdForFavoriting);
        } else  {
          redirectUrl = this.props.siteUrls.favoriteDatasourceUrl(favorite.objectId, favorite.objectIdForFavoriting);
        }
        break;

      case 'project':
        redirectUrl = this.props.siteUrls.favoriteProjectUrl(favorite.objectId);
        break;

      case 'workbook':
        redirectUrl = this.props.siteUrls.favoriteWorkbookUrl(favorite.objectId);
        break;

      case 'view':
        redirectUrl = this.props.siteUrls.redirectToFavoriteViewUrl(favorite.objectId);
        break;

      case 'flow':
        redirectUrl = this.props.siteUrls.favoriteFlowUrl(favorite.objectId);
        break;

      case 'dataRole':
        redirectUrl = this.props.siteUrls.redirectToDataRoleUrl(favorite.objectId);
        break;

      case 'lens':
        redirectUrl = this.props.siteUrls.lensUrl(favorite.objectId);
        break;

      case 'metric':
        redirectUrl = this.props.siteUrls.favoriteMetricUrl(favorite.objectId);
        break;

      case 'virtualConnection':
        redirectUrl = this.props.siteUrls.favoriteVirtualConnectionUrl(favorite.objectId);
        break;

      case 'virtualConnectionTable':
        redirectUrl = this.props.siteUrls.favoriteVirtualConnectionTableUrl(favorite.objectId);
        break;

      case 'collection':
        redirectUrl = this.props.siteUrls.collectionUrl(favorite.objectLuid);
        break;
    }

    return redirectUrl;
  };

  private handleChange = (value: string) => {
    const favorite = this.state.favorites.find((favorite) => {
      return getFavoriteKey(favorite) === value;
    });

    if (!favorite) {
      return;
    }

    const redirectUrl = this.urlForFavorite(favorite);
    if (redirectUrl) {
      Telemetry.sendEvent<FavoritesMenuNavigateToFavoriteEvent>({
        type: TelemetryEvents.FavoritesMenuNavigateToFavorite,
        favoriteItemType: favorite.objectType,
        favoriteItemId: favorite.objectIdForFavoriting, // TODO: once unified data sources have been sorted out, change this to objectId
      });
      Location.instance.setUrl(redirectUrl);
    }

    this.handleClose();
  };

  private onMouseEnter = () => {
    this.updateState({buttonHover: true});
  };

  private onMouseLeave = () => {
    this.updateState({buttonHover: false});
  };

  private getButtonContent = () => {
    const active = this.state.panelOpened || this.state.buttonHover;
    const favoritesSvg = HeaderIcons.FavoritesIcon(active);
    return <span draggable={false}
      style={{
        width: cssVar(CssVariables.headerIconSize),
        height: cssVar(CssVariables.headerIconSize),
        backgroundSize: 'unset',
        backgroundImage: `url(${favoritesSvg})`
      }}
    />;
  };

  private updateState<K extends keyof FavoritesDropdownState>(newState: Pick<FavoritesDropdownState, K>): void {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }
}

export const PageHeaderFavoritesDropdown: React.ComponentType<FavoritesDropdownProps> =
  FavoritesDropdown;
